import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api, type Me } from '../lib/api';
import { BarList, TimeseriesChart } from '../components/Chart';
import { Alert, Card, PageHead, Tile, useLoader } from '../components/ui';

const WINDOWS = [7, 30, 90];

export function Overview({ me }: { me: Me }) {
  const [days, setDays] = useState(30);
  const { data, error, loading } = useLoader(() => api.analytics(days), [days]);

  return (
    <>
      <PageHead
        title="Overview"
        subtitle={`Deliverability for ${me.tenant.name} over the last ${days} days.`}
        actions={
          <div className="row">
            {WINDOWS.map((option) => (
              <button
                key={option}
                type="button"
                className={option === days ? 'btn btn-sm btn-primary' : 'btn btn-sm'}
                onClick={() => setDays(option)}
              >
                {option}d
              </button>
            ))}
          </div>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}

      {me.tenant.status !== 'active' && (
        <Alert kind="warn">
          This account is <strong>{me.tenant.status}</strong>, so sending is paused. A
          complaint rate above 0.1% flags an account for review automatically.
        </Alert>
      )}

      {loading && !data && <div className="empty">Loading analytics…</div>}

      {data && (
        <>
          <div className="tiles">
            <Tile label="Sent" value={data.summary.sent} hint="messages created" />
            <Tile
              label="Delivery rate"
              value={`${data.summary.delivery_rate}%`}
              hint={`${data.summary.delivered} delivered`}
            />
            <Tile
              label="Bounce rate"
              value={`${data.summary.bounce_rate}%`}
              hint={`${data.summary.bounced} bounced`}
            />
            <Tile
              label="Complaint rate"
              value={`${data.summary.complaint_rate}%`}
              hint="0.1% is the risk threshold"
            />
            <Tile
              label="Open rate"
              value={`${data.summary.open_rate}%`}
              hint={`${data.summary.opened} opens`}
            />
            <Tile
              label="Click rate"
              value={`${data.summary.click_rate}%`}
              hint={`${data.summary.clicked} clicks`}
            />
          </div>

          <Card title="Activity">
            <TimeseriesChart points={data.timeseries} />
          </Card>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 16,
            }}
          >
            <Card title="By sending domain">
              <BarList rows={data.by_domain} emptyLabel="No sends in this window." />
            </Card>
            <Card title="By tag">
              <BarList rows={data.by_tag} emptyLabel="No tagged sends in this window." />
            </Card>
          </div>

          <Card>
            <div className="row">
              <div>
                <strong>{data.summary.suppressed_total}</strong> addresses are suppressed.
                <div className="hint">
                  Hard bounces and complaints are added automatically and block future
                  sends to that address.
                </div>
              </div>
              <span className="spacer" />
              <Link className="btn btn-sm" to="/suppressions">
                Review list
              </Link>
            </div>
          </Card>
        </>
      )}
    </>
  );
}
